import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastTone = 'good' | 'danger' | 'info';

export interface ToastOptions {
  title: string;
  description?: string;
  /** Default 'info'. */
  tone?: ToastTone;
  /** ms before auto-dismiss. Default 5000; pass 0 to keep the toast sticky. */
  duration?: number;
}

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- the provider and its hook are one API surface
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const TONE_ICON = { good: CheckCircle2, danger: AlertTriangle, info: Info } as const;
const TONE_TEXT = { good: 'text-good', danger: 'text-danger', info: 'text-info' } as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description, tone = 'info', duration = 5000 }: ToastOptions) => {
      const id = `toast-${++counter.current}`;
      setToasts((prev) => [...prev, { id, title, description, tone }]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="fixed right-4 bottom-4 z-(--z-toast) flex w-80 max-w-[calc(100vw-32px)] flex-col gap-2"
        >
          {toasts.map((item) => {
            const Icon = TONE_ICON[item.tone];
            return (
              <div
                key={item.id}
                role={item.tone === 'danger' ? 'alert' : 'status'}
                className="flex items-start gap-3 rounded-overlay border border-border bg-surface-card p-4 shadow-overlay animate-toast"
              >
                <Icon size={16} className={cn('mt-(--space-hair) shrink-0', TONE_TEXT[item.tone])} />
                <div className="min-w-0 flex-1">
                  <p className="t-label text-ink">{item.title}</p>
                  {item.description && <p className="t-caption mt-1">{item.description}</p>}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => dismiss(item.id)}
                  className="-m-1 shrink-0 rounded-control p-1 text-ink-faint transition-colors hover:text-ink"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
